import { getCurrentInstance } from 'vue';
import type { IDataTableHandler, RowData } from '../types';
import { buttonMessages, dataTableMessages } from '../../../messages';

const RESERVED_EVENTS = new Set([
  'save',
  'delete',
  'selection-change',
  'focus-cell',
]);

interface HandlerOptions {
  skipConfirm?: boolean;
  confirmMessage?: string | ((row: RowData) => string);
  onSave?: (row: RowData) => Promise<RowData>;
  onSaveCompleted?: (row: RowData, result: RowData) => void;
  onDelete?: (row: RowData) => Promise<boolean>;
  onDeleteCompleted?: (row: RowData) => void;
  onSelectionChange?: (payload: any) => void;
  onCellEvent?: (event: string, payload: any) => void;
}

export function useDataTableHandler(
  options: HandlerOptions = {},
): IDataTableHandler {
  const app = getCurrentInstance()?.appContext;

  const confirmDelete = async (row: RowData) => {
    if (options.skipConfirm) {
      return true;
    }

    const message =
      typeof options.confirmMessage === 'function'
        ? options.confirmMessage(row)
        : options.confirmMessage || dataTableMessages.confirmDeleteRow;

    return app!.config.globalProperties.$imodal.confirm({
      severity: 'warning',
      message,
      btnOk: buttonMessages.delete,
    });
  };

  const emit = async (event: string, ...args: any[]) => {
    if (event === 'selection-change') {
      options.onSelectionChange?.(args[0]);
      return Promise.resolve(true);
    }

    if (event === 'delete') {
      const row = args[0]?.row;
      let result: boolean | undefined = true;
      if (options.onDelete) {
        result = await options.onDelete(row);
      }
      if (options.onDeleteCompleted) {
        options.onDeleteCompleted(row);
      }
      return result;
    }

    if (event === 'save') {
      const { row } = args[0];
      let result: RowData = row;
      if (options.onSave) {
        result = await options.onSave(row);
      }
      if (options.onSaveCompleted) {
        options.onSaveCompleted(row, result);
      }
      return Promise.resolve(result);
    }

    if (!RESERVED_EVENTS.has(event) && options.onCellEvent) {
      options.onCellEvent(event, args[0]);
    }

    return Promise.resolve(true);
  };

  return { emit, confirmDelete };
}
